// Trip log creation modal — form-first, with voice as opt-in secondary
import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../api/client';
import TagPicker from './TagPicker';
import MonthPicker from './MonthPicker';
import TagFriendPanel from './TagFriendPanel';
import { tagNamesToPayload } from '../utils/tags';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => CURRENT_YEAR - i);
const MONTH_OPTIONS = [
  { value: '', label: 'Unknown month' },
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export default function TripLogCreator({ isOpen, onClose, onSaved, defaultYear, defaultMonth }) {
  // form state
  const [placeName, setPlaceName] = useState('');
  const [visitYear, setVisitYear] = useState(defaultYear || CURRENT_YEAR);
  const [visitMonth, setVisitMonth] = useState(defaultMonth || '');
  const [note, setNote] = useState('');
  const [rating, setRating] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // companion state
  const [companions, setCompanions] = useState([]);
  const [savedPin, setSavedPin] = useState(null);
  const [savedCompanions, setSavedCompanions] = useState([]);

  // voice state
  const [voiceStep, setVoiceStep] = useState('form'); // 'form' | 'record' | 'processing' | 'tag-friends'
  const [recordingState, setRecordingState] = useState('idle'); // 'idle' | 'recording'
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceError, setVoiceError] = useState('');
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioBlobRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Re-seed year/month whenever the modal is opened with new defaults
  useEffect(() => {
    if (isOpen) {
      setVisitYear(defaultYear || CURRENT_YEAR);
      setVisitMonth(defaultMonth || '');
    }
  }, [isOpen, defaultYear, defaultMonth]);

  function reset() {
    setPlaceName(''); setVisitYear(defaultYear || CURRENT_YEAR); setVisitMonth(defaultMonth || '');
    setNote(''); setRating(null); setSelectedTags([]);
    setError(null); setSaving(false);
    setVoiceStep('form'); setRecordingState('idle'); setRecordingTime(0);
    setVoiceError(''); setTranscript('');
    setCompanions([]); setSavedPin(null); setSavedCompanions([]);
    audioChunksRef.current = []; audioBlobRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
  }

  function stopMic() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function handleClose() { stopMic(); reset(); onClose(); }

  // Escape: close on form/tag-friends steps; return to form on record/processing
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key !== 'Escape') return;
      if (voiceStep === 'record' || voiceStep === 'processing') {
        stopMic();
        setVoiceStep('form');
        setRecordingState('idle');
        setVoiceError('');
      } else {
        handleClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, voiceStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Voice recording ──
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/wav';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        audioBlobRef.current = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      recorder.start();
      setRecordingState('recording');
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
    } catch {
      setVoiceError('Microphone access needed. Check browser permissions.');
    }
  }, []);

  function stopAndTranscribe() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecordingState('idle');
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setTimeout(doTranscribe, 300);
    }
  }

  async function doTranscribe() {
    setVoiceStep('processing');
    const blob = audioBlobRef.current;
    if (!blob) { setVoiceError('No audio captured.'); setVoiceStep('record'); return; }
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'log.webm');
      const token = localStorage.getItem('token');
      const txResp = await fetch(`${API_URL}/voice/transcribe`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const txData = await txResp.json();
      if (!txResp.ok) throw new Error(txData.error || 'Transcription failed');
      const tx = txData.data?.transcript || '';
      setTranscript(tx);

      const strRes = await api.post('/voice/structure', { transcript: tx, context: 'memory' });
      const p = strRes.data?.data || strRes.data || {};
      if (p.place_name) setPlaceName(p.place_name);
      if (p.visit_year) setVisitYear(p.visit_year);
      if (p.visit_month) setVisitMonth(p.visit_month);
      if (p.rating) setRating(p.rating);
      if (Array.isArray(p.tags) && p.tags.length) setSelectedTags(p.tags.slice(0, 3));
      if (Array.isArray(p.companions) && p.companions.length) setCompanions(p.companions);
      if (p.summary) {
        const bullets = Array.isArray(p.summary)
          ? p.summary.map(s => `- ${s}`).join('\n')
          : `- ${p.summary}`;
        setNote(bullets);
      }
      setVoiceStep('form');
    } catch (err) {
      setVoiceError(err.message || 'Could not process — fill in the fields below.');
      setVoiceStep('form');
    }
  }

  function formatTime(s) { return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; }

  function finishAndClose() {
    const pin = savedPin;
    reset();
    if (pin) onSaved(pin);
    else onClose();
  }

  // ── Save ──
  async function handleSave() {
    if (!placeName.trim()) { setError('Place name is required'); return; }
    setSaving(true); setError(null);
    try {
      const result = await api.post('/trip-logs', {
        placeName: placeName.trim(),
        visitYear: visitYear || null,
        visitMonth: visitMonth || null,
        note: note.trim() || null,
        rating: rating || null,
        tags: tagNamesToPayload(selectedTags),
        companions,
      });
      const pin = result.data?.data || result.data;
      setSavedPin(pin);
      setSavedCompanions(pin?.companions || companions);
      setVoiceStep('tag-friends');
      setSaving(false);
    } catch (err) {
      setError(err.message || 'Failed to save trip log');
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  // ── Tag friends step (post-save) ──
  if (voiceStep === 'tag-friends' && savedPin) {
    return (
      <div className="modal-overlay" onClick={finishAndClose}>
        <div className="trip-log-creator" onClick={e => e.stopPropagation()}>
          <div className="trip-log-creator-header">
            <h2>Who did you go with?</h2>
            <button className="modal-close-btn" onClick={finishAndClose} type="button">✕</button>
          </div>
          <div className="trip-log-creator-body">
            <p className="tl-tag-friends-subtitle">
              Tag companions on <strong>{savedPin.placeName || savedPin.place_name}</strong> — they'll get
              a copy added to their own trip log when they join.
            </p>
            <TagFriendPanel
              pinId={savedPin.id}
              companions={savedCompanions}
              pinType="memory"
              onCompanionsChange={setSavedCompanions}
              open={true}
              onOpen={() => {}}
              onClose={finishAndClose}
            />
          </div>
          <div className="trip-log-creator-footer">
            <button className="tl-btn-primary" onClick={finishAndClose} type="button">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Voice record screen ──
  if (voiceStep === 'record') {
    const isRecording = recordingState === 'recording';
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div
          className={`trip-log-creator tl-voice-screen${isRecording ? ' tl-voice-screen-active' : ''}`}
          onClick={e => { e.stopPropagation(); if (isRecording) stopAndTranscribe(); }}
        >
          <button
            className="modal-close-btn tl-voice-close"
            onClick={e => { e.stopPropagation(); handleClose(); }}
            type="button"
          >✕</button>

          <div className="tl-voice-center">
            <div className={`tl-voice-big-circle${isRecording ? ' tl-voice-circle-active' : ''}`}>
              <span className="tl-voice-icon">
                {isRecording ? '🎙' : '✦'}
              </span>
            </div>
            <p className="tl-voice-prompt">
              {isRecording ? 'Listening…' : 'Starting…'}
            </p>
            <p className="tl-voice-hint">
              {isRecording
                ? `${formatTime(recordingTime)} · Tap anywhere to stop`
                : 'Getting microphone ready…'}
            </p>
          </div>

          {voiceError && <p className="tl-error" style={{ textAlign: 'center', padding: '0 1.5rem' }}>{voiceError}</p>}

          <button
            className="tl-voice-back"
            onClick={e => { e.stopPropagation(); stopMic(); setVoiceStep('form'); setVoiceError(''); }}
            type="button"
          >
            Back to form
          </button>
        </div>
      </div>
    );
  }

  // ── Processing screen ──
  if (voiceStep === 'processing') {
    return (
      <div className="modal-overlay">
        <div className="trip-log-creator tl-voice-screen">
          <div className="tl-voice-center">
            <div className="tl-voice-circle tl-voice-processing">
              <div className="tl-voice-spinner" />
            </div>
            <p className="tl-voice-prompt">Organizing your trip…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Form (default) ──
  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="trip-log-creator" onClick={e => e.stopPropagation()}>
        <div className="trip-log-creator-header">
          <h2>Log a Trip</h2>
          {!transcript && (
            <button
              className="tl-voice-pill"
              onClick={() => { setVoiceError(''); setVoiceStep('record'); startRecording(); }}
              type="button"
            >
              ✦ Tell it as a story
            </button>
          )}
          <button className="modal-close-btn" onClick={handleClose} type="button">✕</button>
        </div>

        <div className="trip-log-creator-body">
          {transcript && (
            <details className="tl-transcript-details">
              <summary className="tl-transcript-summary">Your voice note</summary>
              <p className="tl-transcript-text">{transcript}</p>
            </details>
          )}
          {voiceError && <p className="tl-error">{voiceError}</p>}

          <label className="tl-label">
            Where did you go?
            <input
              className="tl-input"
              type="text"
              placeholder="San Diego, CA"
              value={placeName}
              onChange={e => setPlaceName(e.target.value)}
              autoFocus={!transcript}
            />
          </label>

          <div className="tl-row">
            <label className="tl-label tl-label-half">
              Month
              <MonthPicker
                value={visitMonth || null}
                onChange={v => setVisitMonth(v || '')}
              />
            </label>
            <label className="tl-label tl-label-half">
              Year
              <select
                className="tl-input"
                value={visitYear}
                onChange={e => setVisitYear(parseInt(e.target.value))}
              >
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>

          <label className="tl-label">
            What made it memorable?
            <textarea
              className="tl-input tl-textarea"
              placeholder="A quick note about this trip…"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
            />
          </label>

          <div className="tl-label">
            Tags
            <TagPicker selectedTags={selectedTags} onTagsChange={setSelectedTags} maxTags={3} />
          </div>

          <div className="tl-label">
            Rating
            <div className="tl-rating-row">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  className={`tl-rating-btn${rating >= n ? ' tl-rating-btn-active' : ''}`}
                  onClick={() => setRating(rating === n ? null : n)}
                >
                  ❤️
                </button>
              ))}
            </div>
          </div>

          {companions.length > 0 && (
            <div className="tl-label">
              Traveled with
              <div className="tl-companion-chips">
                {companions.map((name, i) => (
                  <span key={i} className="tl-companion-chip">
                    {name}
                    <button
                      type="button"
                      className="tl-companion-chip-remove"
                      onClick={() => setCompanions(c => c.filter((_, j) => j !== i))}
                      aria-label={`Remove ${name}`}
                    >×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && <p className="tl-error">{error}</p>}
        </div>

        <div className="trip-log-creator-footer">
          <button className="tl-btn-secondary" onClick={handleClose} type="button">Cancel</button>
          <button
            className="tl-btn-primary"
            onClick={handleSave}
            disabled={saving || !placeName.trim()}
            type="button"
          >
            {saving ? 'Saving…' : 'Save Trip'}
          </button>
        </div>
      </div>
    </div>
  );
}
